import React from 'react';
import { motion } from 'framer-motion';
import {
    Phone,
    Globe,
    Mail,
    MapPin,
    Send,
    ChevronRight,
    HeartHandshake,
    Map as MapIcon
} from 'lucide-react';

import bg from '../assets/contact.jpg';

export default function Contact() {
    const darkGreen = "#1A2E20";
    const goldAccent = "#D4AF37";
    const creamBg = "#FDFBF7";
    const bgPattern = "https://www.transparenttextures.com/patterns/natural-paper.png";

    const fadeIn = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const contactInfo = [
        {
            icon: <Phone size={18} className="sm:w-5 sm:h-5" />,
            label: "Phone",
            value: "+94 71 922 72 27",
            link: "tel:+94719227227"
        },
        {
            icon: <Mail size={18} className="sm:w-5 sm:h-5" />,
            label: "Email",
            value: "BosethWellnessFoundation@outlook.com",
            link: "BosethWellnessFoundation@outlook.com"
        },
        {
            icon: <Globe size={18} className="sm:w-5 sm:h-5" />,
            label: "Website",
            value: "www.boseth.org",
            link: "https://www.boseth.org"
        },
        {
            icon: <MapPin size={18} className="sm:w-5 sm:h-5" />,
            label: "Physical Address",
            value: "No : 612, Yowun Mawatha, Thewaththa, Ragama, Sri Lanka",
            link: "https://www.google.com/maps/search/?api=1&query=No+612+Yowun+Mawatha+Thewaththa+Ragama+Sri+Lanka"
        }
    ];

    return (
        <div style={{ backgroundColor: creamBg, color: darkGreen }} className="min-h-screen font-sans selection:bg-[#B89561] selection:text-white relative overflow-hidden">
            {/* PAPER TEXTURE OVERLAY */}
            <div className="absolute inset-0 pointer-events-none opacity-40 z-10" style={{ backgroundImage: `url(${bgPattern})` }}></div>

            {/* 1. HERO SECTION */}
            <section className="relative h-[55vh] md:h-[60vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {/* Background set to local asset file */}
                    <img src={bg} className="w-full h-full object-cover" alt="Contact Hero" />
                    <div className="absolute inset-0 bg-[#1A2E20]/50"></div>
                </div>

                <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 w-full pt-20">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                        <div className="flex items-center gap-2 text-white/90 text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-4 sm:mb-6 font-bold">
                            <span>Home</span>
                            <ChevronRight size={12} className="text-[#B89561]" />
                            <span className="text-white font-medium">Contact Us</span>
                        </div>
                        <h1 className="font-serif text-4xl sm:text-6xl md:text-8xl text-white leading-tight">
                            Reach Out to <br/>
                            <span className="italic font-light text-[#D4AF37]">Boseth</span>
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* 2. INTRO & DETAILS */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6 z-20">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">

                        {/* LEFT: CONTENT & INFO */}
                        <motion.div {...fadeIn} className="text-center sm:text-left max-w-[340px] sm:max-w-full mx-auto w-full">
                            <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px] block mb-4 sm:mb-6">We’d Love to Hear from You</span>
                            <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#1A2E20] mb-6 sm:mb-8 leading-tight">We’re Here to Listen, Support, and Guide You.</h2>
                            <p className="text-gray-600 text-sm sm:text-base md:text-lg font-light leading-relaxed mb-8 sm:mb-12">
                                Whether you’re seeking healing, volunteering opportunities, or just want to learn more about our work—Boseth Spiritual Oasis welcomes your presence and connection.
                            </p>

                            <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 text-left">
                                {contactInfo.map((info, i) => (
                                    <div key={i} className="group flex flex-col items-center sm:items-start text-center sm:text-left">
                                        <div className="flex items-center gap-3 sm:gap-4 mb-2 sm:mb-3">
                                            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-[#B89561]/30 flex items-center justify-center text-[#B89561] group-hover:bg-[#B89561] group-hover:text-white transition-all duration-500 shrink-0">
                                                {info.icon}
                                            </div>
                                            <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400">{info.label}</span>
                                        </div>
                                        <a href={info.link} target="_blank" rel="noopener noreferrer" className="text-sm sm:text-base md:text-lg font-medium hover:text-[#B89561] transition-colors break-words max-w-full">
                                            {info.value}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        {/* RIGHT: CONTACT FORM */}
                        <motion.div
                            {...fadeIn}
                            className="bg-white p-6 sm:p-10 md:p-12 rounded-[1.5rem] sm:rounded-[3rem] shadow-2xl relative overflow-hidden max-w-[340px] sm:max-w-full mx-auto w-full"
                        >
                            <div className="relative z-10">
                                <h3 className="font-serif text-2xl sm:text-3xl mb-6 sm:mb-8 text-center sm:text-left">Send a Message</h3>
                                <form className="space-y-4 sm:space-y-6">
                                    <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                                        <div className="space-y-1.5 sm:space-y-2">
                                            <label className="text-[9px] sm:text-[10px] uppercase tracking-widest font-bold text-gray-400 ml-4">Name *</label>
                                            <input type="text" className="w-full bg-[#FDFBF7] border-none rounded-full px-5 py-3.5 sm:px-6 sm:py-4 text-sm focus:ring-2 focus:ring-[#B89561] transition-all" placeholder="Your Name" required />
                                        </div>
                                        <div className="space-y-1.5 sm:space-y-2">
                                            <label className="text-[9px] sm:text-[10px] uppercase tracking-widest font-bold text-gray-400 ml-4">Email *</label>
                                            <input type="email" className="w-full bg-[#FDFBF7] border-none rounded-full px-5 py-3.5 sm:px-6 sm:py-4 text-sm focus:ring-2 focus:ring-[#B89561] transition-all" placeholder="Your Email" required />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5 sm:space-y-2">
                                        <label className="text-[9px] sm:text-[10px] uppercase tracking-widest font-bold text-gray-400 ml-4">Subject</label>
                                        <input type="text" className="w-full bg-[#FDFBF7] border-none rounded-full px-5 py-3.5 sm:px-6 sm:py-4 text-sm focus:ring-2 focus:ring-[#B89561] transition-all" placeholder="How can we help?" />
                                    </div>
                                    <div className="space-y-1.5 sm:space-y-2">
                                        <label className="text-[9px] sm:text-[10px] uppercase tracking-widest font-bold text-gray-400 ml-4">Message *</label>
                                        <textarea rows="4" className="w-full bg-[#FDFBF7] border-none rounded-[1.5rem] sm:rounded-[2rem] px-5 py-3.5 sm:px-6 sm:py-4 text-sm focus:ring-2 focus:ring-[#B89561] transition-all" placeholder="Your message..." required></textarea>
                                    </div>
                                    <button
                                        type="submit"
                                        style={{ backgroundColor: darkGreen }}
                                        className="w-full text-white py-4 sm:py-5 rounded-full font-bold uppercase text-[11px] sm:text-[12px] tracking-[0.2em] hover:bg-[#B89561] transition-all duration-500 flex items-center justify-center gap-3 group shadow-lg"
                                    >
                                        Submit Message
                                        <Send size={15} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                    </button>
                                </form>
                            </div>
                        </motion.div>

                    </div>
                </div>
            </section>

            {/* 3. MAP LOCATION SECTION */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6 z-20 bg-white/30">
                <div className="max-w-7xl mx-auto">
                    <motion.div {...fadeIn} className="space-y-8 sm:space-y-10">
                        <div className="text-center">
                            <div className="flex justify-center mb-3 sm:mb-4">
                                <MapIcon color={goldAccent} size={28} className="sm:w-8 sm:h-8" />
                            </div>
                            <h2 className="font-serif text-2xl sm:text-4xl md:text-5xl text-[#1A2E20]">Visit Our Oasis</h2>
                            <p className="text-gray-500 mt-2 sm:mt-4 tracking-widest uppercase text-[9px] sm:text-[10px] font-bold">Ragama, Sri Lanka</p>
                        </div>

                        <div className="relative rounded-[1.5rem] sm:rounded-[3rem] overflow-hidden shadow-2xl border-4 sm:border-8 border-white h-[350px] sm:h-[500px] max-w-[340px] sm:max-w-full mx-auto w-full">
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3959.7513115914207!2d79.93514429999999!3d7.0384867!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae2f900741b8c1d%3A0x59ea9110b40feb32!2sBoseth!5e0!3m2!1sen!2slk!4v1776916587994!5m2!1sen!2slk"
                                width="100%"
                                height="100%"
                                style={{ border: 0, filter: 'grayscale(0.2) contrast(1.1)' }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Boseth Location"
                            ></iframe>
                            <div className="absolute inset-0 pointer-events-none border-[1px] border-[#1A2E20]/10 rounded-[1.5rem] sm:rounded-[3rem]"></div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 4. QUOTE / VALUES SECTION */}
            <section className="relative py-16 sm:py-24 bg-[#1A2E20] text-white overflow-hidden px-4 sm:px-6">
                <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: `url(${bgPattern})` }}></div>
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <motion.div {...fadeIn}>
                        <div className="flex justify-center mb-6 sm:mb-8">
                            <HeartHandshake color={goldAccent} size={40} className="sm:w-12 sm:h-12" />
                        </div>
                        <h2 className="font-serif text-2xl sm:text-4xl md:text-5xl mb-4 sm:mb-8">We Value Connection</h2>
                        <p className="text-gray-300 text-base sm:text-lg md:text-xl font-light leading-relaxed mb-6 sm:mb-10 italic">
                            “Open Hearts, Clear Words, True Connection.”
                        </p>
                        <p className="text-gray-400 text-xs sm:text-sm md:text-lg font-light max-w-2xl mx-auto leading-relaxed">
                            At Boseth, communication is sacred. Your messages are received with care and responded to with kindness. Whether it’s a question, feedback, or a request for healing, we are here for you.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* FOOTER-LIKE SPACE */}
            <div className="py-8 sm:py-12 text-center relative z-10 px-4">
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] sm:tracking-[0.5em] font-bold text-gray-400">Connect, Learn, Belong Together</p>
            </div>
        </div>
    );
}